import { useCallback, useEffect } from 'react';
import { useMatch } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectLayoutDirection,
  selectLTRGridLayout,
  selectPreviewSize,
  selectRTLGridLayout,
} from '@/store/slices/layout-grid/selectors';
import { selectProjectThemeMode } from '@/store/slices/project/selectors';
import { EPaletteModes } from '@/types/theme-packs.types';
import { PATHNAMES } from '@/utils/pathnames';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { setTrackedBodyAttributes } from '@/store/slices/layout-grid';
import useCreateThemeColorCssVariables from '@/utils/colors/use-create-theme-color-css-variables';

const GlobalDocumentHead = () => {
  const viewSize = useAppSelector(selectPreviewSize);
  const ltr = useAppSelector(selectLTRGridLayout);
  const rtl = useAppSelector(selectRTLGridLayout);
  const layoutGridDir = useAppSelector(selectLayoutDirection);
  const projectThemeMode = useAppSelector(selectProjectThemeMode);
  const userChannel = useAppSelector(selectUserChannel);

  const dispatch = useAppDispatch();

  const channelType = userChannel?.type;
  const channelIsWebinar = isWebinarChannel(channelType) || window.location.pathname.startsWith('/webinarplus');

  // only change theme mode if we are in the edit project paths
  const matchedEditProjectPath = useMatch(PATHNAMES.Edit.Root);
  const themeMode =
    (!!matchedEditProjectPath && projectThemeMode) || (channelIsWebinar ? EPaletteModes.Light : EPaletteModes.Dark);

  const { colorVariablesList, contrastColorsList, blendColorsList, createProjectThemeOverChannelThemeColorVariables } =
    useCreateThemeColorCssVariables();

  const createBodyAttributes = useCallback(() => {
    const body = document.querySelector('body');
    if (body) {
      body.setAttribute('data-preview-size', viewSize);
      body.setAttribute('data-layout-grid-dir', layoutGridDir);
      body.setAttribute('data-admin-theme', themeMode);
      if (channelType) {
        body.setAttribute('data-channel-type', channelType);
      }
      // Putting back into redux. Why? Because there are areas of the app where we mnight not want to rely
      // on workingProject to select the theme mode. This redux slice is "global" and we can select the theme mode
      // from there if needed. The only other alternative is to track mutations on the body attribute, which isn't ideal
      // Note: this has been tested and only re-renders once everytime the theme mode changes.
      dispatch(setTrackedBodyAttributes({ theme: themeMode }));
    }
  }, [viewSize, layoutGridDir, themeMode, channelType, dispatch]);

  const createStyles = useCallback(() => {
    const styleId = 'global-document-head-style';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = `
      :root {
        --layout-grid: ${ltr};
        --layout-grid-rtl: ${rtl};

        ${colorVariablesList}

        ${contrastColorsList}

        ${blendColorsList}
      }
    `;
    /* createProjectThemeOverChannelThemeColorVariables must be handled after createStyles, because we need all the other variables to be set first  */
    style.textContent += `
      :root {
        ${createProjectThemeOverChannelThemeColorVariables(themeMode)}
      }
    `;
  }, [
    ltr,
    rtl,
    colorVariablesList,
    contrastColorsList,
    blendColorsList,
    createProjectThemeOverChannelThemeColorVariables,
    themeMode,
  ]);

  useEffect(() => {
    // react-helmet-async doesn't properly set data attributes in all scenarios,
    // especially when <GlobalDocumentHead /> is not rendered in the main app component.
    // But, since we want to be able to use react-router-dom's useMatch hook, we cannot render
    // GlobalDocumentHead in the main app component, we need to render it in the child component so that
    // it has access to react-router's context.
    // So we manually set all the data attributes on the body tag and the style tag
    createBodyAttributes();
    createStyles();
    document.title = channelIsWebinar ? 'Webinar+' : 'Brandlive';
  }, [createBodyAttributes, createStyles, channelIsWebinar]);

  return null;
};

export default GlobalDocumentHead;
